/**
 * Game controller.
 *
 * Owns the frame loop, the screen state machine and every subsystem. Screens in
 * `ui/screens.ts` are pure drawing functions that read and mutate this object
 * through the `GameApi` interface, which keeps navigation in exactly one place.
 */

import { clamp } from '@shared/math.js';
import { getCar, CARS } from '@shared/cars.js';
import { TRACKS } from '@shared/tracks.js';
import { UPGRADES } from '@shared/upgrades.js';
import { AI_ORDER, type AIDifficulty } from '@shared/ai.js';
import { migrateSave, type GameSettings } from '@shared/save.js';
import type { RaceModeId } from '@shared/net.js';

import { InputManager } from './core/input.js';
import { Renderer } from './render/renderer.js';
import { AudioEngine } from './audio/audio.js';
import { Store, ApiError } from './store.js';
import { NetClient } from './net/client.js';
import { RaceSession } from './race/race-session.js';
import { Ui } from './ui/ui.js';
import { TextEntry } from './ui/text-entry.js';
import {
  applyMenuScroll,
  drawConfirm, drawGarage, drawLobby, drawLogin, drawMainMenu, drawMultiplayer,
  drawPauseOverlay, drawRaceSetup, drawResults, drawSettings, drawStats, drawToasts,
  drawTracks, drawUpgrades, focusLoginField, loginFieldRects,
} from './ui/screens.js';
import { drawHud } from './ui/hud.js';
import type { GameApi, ResultsView, ScreenId, Toast } from './ui/state.js';

type CountdownHold = 'none' | 'running';

export class Game implements GameApi {
  readonly renderer: Renderer;
  readonly input: InputManager;
  readonly audio = new AudioEngine();
  readonly store = new Store();
  readonly net = new NetClient();
  readonly ui: Ui;
  readonly text: TextEntry;

  screen: ScreenId = 'boot';
  previousScreen: ScreenId = 'menu';
  toasts: Toast[] = [];
  confirm: GameApi['confirm'] = null;

  login: GameApi['login'] = { username: '', password: '', busy: false, error: '', restoreUrl: '' };
  loginFocus: 'username' | 'password' | null = null;

  setup: GameApi['setup'] = {
    mode: 'quick-race',
    trackId: TRACKS[0]!.id,
    laps: 3,
    aiCount: 5,
    difficulty: 'normal',
    eliminationInterval: 30,
    checkpointTarget: 6,
  };

  garageIndex = 0;
  garageTab = 0;
  upgradeIndex = 0;
  paintIndex = 0;
  purchaseBusy = false;
  purchaseError = '';

  trackIndex = 0;
  leaderboard: GameApi['leaderboard'] = [];
  leaderboardTrack = '';
  leaderboardLoading = false;

  settingsTab = 0;
  rebindTarget: string | null = null;

  lobbyDraft: GameApi['lobbyDraft'] = {
    code: '',
    name: 'Private room',
    isPrivate: true,
    maxPlayers: 8,
    aiCount: 3,
    difficulty: 'normal',
    trackId: TRACKS[0]!.id,
    laps: 3,
    mode: 'online',
  };
  lobbyCodeEntry = '';
  lobbyCodeFocus = false;
  mpBusy = false;
  /** Pending create/join request, so the server's answer can settle it. */
  private lobbyIntent: 'create' | 'join' | null = null;
  private lobbyTimer: number | null = null;

  results: ResultsView | null = null;

  frameTime = 0;
  steeringSensitivity = 1;

  /** Integer scale the world is currently drawn at (mirrors the setting). */
  get pixelScale(): number {
    return this.renderer.pixelScale;
  }

  private lastSteering = 0;
  private cameraMode = 0;
  private paused = false;

  private race: RaceSession | null = null;
  private raceHoldTimer: number | null = null;
  private fps = 0;
  private frameCount = 0;
  private fpsTimer = 0;
  private lastFrame = performance.now();
  private running = false;
  private bootMessage = 'Loading…';
  private previousLoginFocus: 'username' | 'password' | null = null;
  private boundKeyHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(private readonly root: HTMLElement, canvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas);
    this.input = new InputManager(canvas);
    this.ui = new Ui(this.renderer.ctx, this.input.pointer);
    this.text = new TextEntry(root);
  }

  // ------------------------------------------------------------------- boot
  async boot(): Promise<void> {
    const bootEl = document.getElementById('boot');
    const statusEl = document.getElementById('boot-status');

    const setStatus = (text: string): void => {
      this.bootMessage = text;
      if (statusEl) statusEl.textContent = text;
    };

    try {
      setStatus('Contacting the server…');
      await this.store.fetchBootstrap();

      setStatus('Restoring your session…');
      const restored = await this.store.restore();

      this.applySettings();
      this.setup.trackId = this.store.save.lastTrack || TRACKS[0]!.id;
      this.setup.mode = (this.store.save.lastMode as RaceModeId) || 'quick-race';
      this.garageIndex = Math.max(0, CARS.findIndex((c) => c.id === this.store.save.selectedCar));

      this.screen = restored ? 'menu' : 'login';
      if (restored) {
        this.audio.startMusic();
      }

      bootEl?.classList.add('hidden');
      setTimeout(() => bootEl?.remove(), 400);

      this.net.onMessage((msg) => {
        if (msg.t === 'start') this.onOnlineRaceStarted();
        if (msg.t === 'lobby') {
          this.audio.sfx('click');
          this.settleLobbyIntent();
        }
        if (msg.t === 'error' && msg.code !== 'payout') this.failLobbyIntent(msg.message);
        if (msg.t === 'chat') this.toast(`${msg.from}: ${msg.text}`);
      });

      this.running = true;
      requestAnimationFrame(this.frame);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setStatus(`Could not start: ${message}`);
      if (statusEl) statusEl.className = 'boot-error';
      console.error('[boot]', err);
    }
  }

  // ------------------------------------------------------------------ frame
  private frame = (now: number): void => {
    if (!this.running) return;
    const rawDt = (now - this.lastFrame) / 1000;
    this.lastFrame = now;
    // Clamp the delta: a backgrounded tab or a long GC pause must not teleport
    // every car through a wall.
    const dt = clamp(rawDt, 0, 0.1);
    this.frameTime = dt;

    this.frameCount++;
    this.fpsTimer += dt;
    if (this.fpsTimer >= 0.5) {
      this.fps = Math.round(this.frameCount / this.fpsTimer);
      this.frameCount = 0;
      this.fpsTimer = 0;
    }

    this.step(dt);
    // A layout change that the resize observers missed (fonts finishing
    // loading, a scrollbar appearing, a browser restore) would leave the UI
    // drawing at one size and hit-testing at another, so re-measure cheaply.
    if (this.renderer.sizeIsStale()) this.resize();
    this.draw(dt);
    this.input.endFrame();
    requestAnimationFrame(this.frame);
  };

  private step(dt: number): void {
    this.ui.pointer.inside = true;

    // Rebinding: the next key press becomes the new binding.
    if (this.rebindTarget) {
      const code = this.firstPressedKey();
      if (code && code !== 'Escape') {
        this.store.updateControls({ [this.rebindTarget]: code } as Record<string, string>);
        this.rebindTarget = null;
        this.toast('Binding updated.');
        void this.store.pushSave();
      } else if (code === 'Escape') {
        this.rebindTarget = null;
      }
      return;
    }

    if (this.screen === 'race') {
      if (this.input.wantsPause()) this.setPaused(!this.paused);
      if (this.paused) return;
      this.stepRace(dt);
      return;
    }

    if (this.screen === 'login') this.syncLoginField();
    else if (this.previousLoginFocus) this.clearLoginFocus();
  }

  private stepRace(dt: number): void {
    const race = this.race;
    if (!race) return;
    this.updateTouch();
    const sample = this.input.sample(dt, this.lastSteering);
    this.lastSteering = sample.input.steer;
    if (this.store.settings.autoAccelerate) sample.input.throttle = Math.max(sample.input.throttle, 0.85);
    if (sample.camera) this.cameraMode = (this.cameraMode + 1) % 2;
    race.camera.userZoom = this.store.settings.cameraZoom * (this.cameraMode === 0 ? 1 : 0.82);
    race.update(dt, sample.input, { reset: sample.reset });

    if (race.outcome && !this.raceHoldTimer) {
      // Let the finish line sink in before the results screen appears.
      const delay = race.playerFinished ? 2600 : 600;
      this.raceHoldTimer = window.setTimeout(() => {
        this.showResults(race);
      }, delay);
    }
  }

  private draw(dt: number): void {
    const renderer = this.renderer;
    const ui = this.ui;

    if (this.screen === 'race' && this.race) {
      this.drawRace(dt);
      if (this.paused) {
        renderer.beginScreenSpace();
        ui.beginFrame(renderer.viewWidth, renderer.viewHeight, this.navKeys());
        drawPauseOverlay(this);
        drawToasts(this);
        ui.endFrame();
      }
      return;
    }

    // Every menu frame starts from native screen space. Without this the canvas
    // still carries whatever transform the last world frame left behind (or the
    // identity transform on a cold start), so the menus were drawn at the wrong
    // scale and every button's hit box landed somewhere else on screen.
    renderer.beginScreenSpace();
    const keys = this.navKeys();
    ui.beginFrame(renderer.viewWidth, renderer.viewHeight, keys);
    // Page/Home/End pan a screen that is taller than the window. The scroll
    // range is from the previous frame, which is one frame stale at most.
    applyMenuScroll(ui, keys);
    switch (this.screen) {
      case 'login':
        drawLogin(this);
        break;
      case 'play':
        drawRaceSetup(this);
        break;
      case 'garage':
        drawGarage(this);
        break;
      case 'upgrades':
        drawUpgrades(this);
        break;
      case 'tracks':
        drawTracks(this);
        break;
      case 'stats':
        drawStats(this);
        break;
      case 'settings':
        drawSettings(this);
        break;
      case 'multiplayer':
        drawMultiplayer(this);
        break;
      case 'lobby':
        drawLobby(this);
        break;
      case 'results':
        drawResults(this);
        break;
      case 'menu':
      default:
        drawMainMenu(this);
        break;
    }
    // Toasts and the confirm dialog are overlays, not screen content: they are
    // pinned to the window, so the scroll clip has to come off first or a toast
    // near the bottom edge would be cut in half.
    ui.endScroll();
    drawConfirm(this);
    drawToasts(this);
    ui.endFrame();
  }

  private drawRace(dt: number): void {
    const race = this.race;
    if (!race) return;
    const renderer = this.renderer;
    const hud = race.hud();

    renderer.drawWorld({
      baked: race.baked,
      skids: race.skids,
      particles: race.particles,
      camera: race.camera,
      cars: race.carDraws(),
      racingLine: this.store.settings.showRacingLine ? this.race!.track.racingLine : null,
      checkpoints: this.store.settings.showRacingLine ? this.race!.track.checkpoints : null,
    });

    const ui = this.ui;
    renderer.beginScreenSpace();
    ui.beginFrame(renderer.viewWidth, renderer.viewHeight, this.navKeys());
    drawHud(ui, renderer, race, hud, race.camera, {
      paused: this.paused,
      showRacingLine: this.store.settings.showRacingLine,
      touchEnabled: this.input.touch.enabled,
      showMinimap: this.store.settings.minimap,
      fps: this.fps,
      ping: this.net.ping,
      cameraMode: this.cameraMode,
    }, dt);
    ui.endFrame();
  }

  /**
   * Maps the on-screen touch layout drawn by the HUD onto the input manager.
   * Layout: a steering dial bottom-left, and GO / BRAKE / SLIDE buttons
   * bottom-right, all sized relative to the UI scale so they work on phones and
   * tablets alike.
   */
  private updateTouch(): void {
    const touch = this.input.touch;
    if (!touch.enabled) return;
    touch.steer = 0;
    touch.throttle = 0;
    touch.brake = 0;
    touch.handbrake = false;
    if (!this.input.pointer.down) return;

    const s = this.ui.scale;
    const px = this.input.pointer.x;
    const py = this.input.pointer.y;
    const w = this.renderer.viewWidth;
    const h = this.renderer.viewHeight;

    const dialX = 92 * s;
    const dialY = h - 110 * s;
    const dialR = 64 * s;
    if (Math.hypot(px - dialX, py - dialY) <= dialR * 1.35) {
      touch.steer = clamp((px - dialX) / dialR, -1, 1);
      return;
    }

    const buttons: Array<[number, number, number, () => void]> = [
      [w - 210 * s, h - 150 * s, 40 * s, () => { touch.throttle = 1; }],
      [w - 150 * s, h - 74 * s, 40 * s, () => { touch.brake = 1; }],
      [w - 268 * s, h - 74 * s, 40 * s, () => { touch.handbrake = true; }],
    ];
    for (const [bx, by, r, apply] of buttons) {
      if (Math.hypot(px - bx, py - by) <= r) {
        apply();
        return;
      }
    }
  }

  private navKeys(): {
    up: boolean; down: boolean; left: boolean; right: boolean; activate: boolean;
    pageUp: boolean; pageDown: boolean; home: boolean; end: boolean;
  } {
    return {
      up: this.input.wasPressed('ArrowUp') || this.input.wasPressed('KeyW'),
      down: this.input.wasPressed('ArrowDown') || this.input.wasPressed('KeyS'),
      left: this.input.wasPressed('ArrowLeft') || this.input.wasPressed('KeyA'),
      right: this.input.wasPressed('ArrowRight') || this.input.wasPressed('KeyD'),
      activate: this.input.wasPressed('Enter') || this.input.wasPressed('NumpadEnter') || this.input.wasPressed('Space'),
      pageUp: this.input.wasPressed('PageUp'),
      pageDown: this.input.wasPressed('PageDown'),
      home: this.input.wasPressed('Home'),
      end: this.input.wasPressed('End'),
    };
  }

  private firstPressedKey(): string | null {
    const candidates = [
      'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyR', 'KeyC', 'Space', 'ShiftLeft', 'ShiftRight',
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Tab', 'Escape',
    ];
    for (const code of candidates) if (this.input.wasPressed(code)) return code;
    for (const code of ['KeyQ', 'KeyE', 'KeyF', 'KeyG', 'KeyZ', 'KeyX', 'ControlLeft', 'AltLeft']) {
      if (this.input.wasPressed(code)) return code;
    }
    return null;
  }

  // ------------------------------------------------------------- navigation
  /**
   * Pauses or resumes an in-progress race.
   *
   * The pause overlay is a set of clickable buttons, so the hidden gameplay
   * cursor has to come back — otherwise Escape left the player on a menu they
   * could not see the pointer for.
   */
  setPaused(value: boolean): void {
    if (this.paused === value) return;
    this.paused = value;
    this.input.textEntryActive = false;
    this.renderer.canvas.classList.toggle('playing', !value);
    this.ui.resetNavigation();
    if (value) this.audio.silence();
    else this.audio.unlock();
  }

  /**
   * Target of every screen's "← Back" action.
   *
   * A menu reached from the pause overlay belongs to the race that is still
   * paused behind it, so Back returns there rather than dumping the player in the
   * main menu with their race silently abandoned.
   */
  back(): void {
    if (this.race && this.paused && this.screen !== 'race') {
      this.show('race');
      // Stay paused: Back should land on the pause overlay for another choice,
      // not drop the player straight back into a moving car.
      this.setPaused(true);
      return;
    }
    this.show('menu');
  }

  show(screen: ScreenId): void {
    // Entering 'race' is always (re-)applied: it doubles as "resume", so it has
    // to clear the pause state even when we are already on the race screen.
    if (screen === this.screen && screen !== 'race') return;
    if (this.screen === 'login') this.clearLoginFocus();
    this.previousScreen = this.screen;
    this.screen = screen;
    this.ui.resetNavigation();
    if (screen === 'race') {
      this.paused = false;
      this.input.textEntryActive = false;
      this.renderer.canvas.classList.add('playing');
    } else {
      this.renderer.canvas.classList.remove('playing');
      this.audio.silence();
      if (this.previousScreen === 'race') this.audio.startMusic();
    }
    if (screen === 'tracks') {
      const track = TRACKS[clamp(this.trackIndex, 0, TRACKS.length - 1)]!;
      this.loadLeaderboard(track.id);
    }
    if (screen === 'upgrades') {
      this.upgradeIndex = 0;
      this.purchaseError = '';
    }
    if (screen === 'multiplayer') {
      if (!this.net.connected) this.net.connect();
      this.mpBusy = false;
    }
    this.audio.unlock();
  }

  toast(message: string, bad = false): void {
    this.toasts.push({ message, bad, at: Date.now() });
    if (this.toasts.length > 5) this.toasts.shift();
  }

  requestConfirm(dialog: GameApi['confirm']): void {
    this.confirm = dialog;
  }

  applySettings(): void {
    const settings = this.store.settings;
    this.audio.setVolumes(settings.masterVolume, settings.musicVolume, settings.sfxVolume, false);
    this.text.position({ x: -1000, y: -1000, w: 1, h: 1 });
    this.input.steeringSensitivity = this.steeringSensitivity;
    this.input.setBindings(this.store.controls);
    this.resize();
  }

  setSteeringSensitivity(value: number): void {
    this.steeringSensitivity = value;
    this.input.steeringSensitivity = value;
  }

  resize(): void {
    this.renderer.resize();
    this.renderer.setBaseScale(this.store.settings.pixelScale);
    if (this.race) {
      this.race.camera.userZoom = this.store.settings.cameraZoom;
      this.race.camera.shakeEnabled = this.store.settings.screenShake && !this.store.settings.reducedMotion;
      this.race.particles.quality = this.store.settings.reducedMotion ? 0.5 : 1;
    }
  }

  // ------------------------------------------------------------------ login
  private syncLoginField(): void {
    const field = this.loginFocus;
    this.input.textEntryActive = field !== null;
    if (field === null) {
      this.previousLoginFocus = null;
      return;
    }
    const rects = loginFieldRects(this.ui);
    // Moving between fields re-configures the DOM input (type, placeholder and
    // submit behaviour all change), so the hand-off is explicit.
    if (field !== this.previousLoginFocus) {
      this.previousLoginFocus = field;
      focusLoginField(this, field, rects.userRect, rects.passRect);
      return;
    }
    // Re-position every frame so the field follows a resize.
    this.text.position(field === 'username' ? rects.userRect : rects.passRect);
  }

  private clearLoginFocus(): void {
    this.loginFocus = null;
    this.previousLoginFocus = null;
    this.input.textEntryActive = false;
    this.text.blur();
  }

  async signIn(): Promise<void> {
    if (this.login.busy) return;
    if (!this.login.username.trim() || !this.login.password) {
      this.login.error = 'Enter your username and password.';
      return;
    }
    this.login.busy = true;
    this.login.error = '';
    try {
      await this.store.login(this.login.username.trim(), this.login.password);
      this.clearLoginFocus();
      this.login.password = '';
      this.show('menu');
      this.audio.unlock();
      this.audio.startMusic();
      this.audio.sfx('unlock');
      this.toast(`Welcome back, ${this.store.user?.displayName ?? ''}.`);
    } catch (err) {
      this.login.error = err instanceof ApiError ? err.message : 'Sign in failed.';
      this.audio.sfx('error');
    } finally {
      this.login.busy = false;
    }
  }

  async signOut(): Promise<void> {
    await this.store.logout();
    this.net.disconnect();
    this.audio.stopMusic();
    this.show('login');
    this.toast('Signed out.');
  }

  // ------------------------------------------------------------------ races
  beginRace(): void {
    this.store.rememberLastChoice(this.setup.trackId, this.setup.mode);
    void this.store.pushSave();
    const previousBest = this.store.save.bestLaps[this.setup.trackId] ?? 0;
    const carId = this.store.save.selectedCar;
    const car = getCar(carId);
    this.race = new RaceSession(
      {
        mode: this.setup.mode,
        trackId: this.setup.trackId,
        laps: this.setup.laps,
        // Solo formats have no opponents by definition.
        aiCount:
          this.setup.mode === 'time-trial' || this.setup.mode === 'practice' || this.setup.mode === 'drift'
            ? 0
            : this.setup.aiCount,
        aiDifficulty: this.setup.difficulty,
        seed: Math.floor(Math.random() * 2 ** 31),
        player: {
          name: this.store.user?.displayName ?? 'You',
          carId,
          color: this.store.save.paint[carId] ?? car.palettes[0]!,
          upgrades: this.store.save.upgrades[carId] ?? {},
        },
        displayName: this.store.user?.displayName ?? 'You',
        settings: this.store.settings,
        previousBestLap: previousBest,
        eliminationInterval: this.setup.eliminationInterval,
        checkpointTarget: this.setup.checkpointTarget,
      },
      this.audio,
    );
    this.raceHoldTimer = null;
    this.cameraMode = 0;
    this.lastSteering = 0;
    this.paused = false;
    this.show('race');
  }

  restartRace(): void {
    // An online race belongs to the room, not to this client: it is restarted by
    // the server for everybody, and only the room leader may ask for it.
    if (this.race?.isOnline) {
      this.results = null;
      this.raceHoldTimer = null;
      if (this.net.lobby && this.net.isHost()) {
        this.toast('Starting another race…');
        this.net.startRace();
        this.show('lobby');
      } else if (this.net.lobby) {
        this.toast('Only the room leader can start another race.', true);
        this.show('lobby');
      } else {
        this.show('multiplayer');
      }
      return;
    }
    this.results = null;
    this.raceHoldTimer = null;
    if (this.net.lobby) {
      this.net.leaveLobby();
    }
    this.beginRace();
  }

  quitRace(): void {
    this.race?.dispose();
    this.race = null;
    if (this.raceHoldTimer) window.clearTimeout(this.raceHoldTimer);
    this.raceHoldTimer = null;
    this.results = null;
    this.paused = false;
    this.audio.silence();
    this.show('menu');
    if (this.net.connected && this.net.lobby) this.net.leaveLobby();
  }

  private showResults(race: RaceSession): void {
    const outcome = race.outcome;
    if (!outcome) return;
    this.results = {
      ...outcome,
      serverRewards: null,
      newAchievements: [],
      personalBest: outcome.rewards.newBestLap,
      submitted: false,
      submitError: '',
      level: null,
      offline: !this.store.signedIn,
    };
    this.paused = false;
    this.show('results');
    void this.submitResultsIfNeeded();
  }

  async submitResultsIfNeeded(): Promise<void> {
    const results = this.results;
    if (!results || results.submitted) return;
    results.submitted = true;
    if (!this.store.signedIn) {
      results.offline = true;
      return;
    }
    const row = results.playerResult;
    try {
      const outcome = await this.store.submitRace({
        trackId: results.trackId,
        mode: results.mode,
        position: row.position,
        racers: results.results.length,
        laps: row.laps,
        bestLap: row.bestLap,
        raceTime: row.time,
        driftScore: row.driftScore,
        respawns: row.respawns,
        finished: row.finished,
        topSpeed: row.topSpeed,
        difficulty: results.difficulty,
      });
      if (!outcome) {
        results.offline = true;
        results.submitError = 'Saved locally — the server could not be reached.';
        return;
      }
      results.serverRewards = outcome.rewards;
      results.newAchievements = outcome.newAchievements;
      results.personalBest = outcome.personalBest;
      results.level = outcome.level;
      if (outcome.newAchievements.length > 0) this.audio.sfx('achievement');
    } catch (err) {
      results.submitError = err instanceof ApiError ? err.message : 'Could not submit the result.';
    }
  }

  // ----------------------------------------------------------------- garage
  openGarage(): void {
    const index = CARS.findIndex((c) => c.id === this.store.save.selectedCar);
    if (index >= 0) this.garageIndex = index;
    this.show('garage');
  }

  async buySelectedCar(): Promise<void> {
    const car = CARS[clamp(this.garageIndex, 0, CARS.length - 1)]!;
    this.purchaseBusy = true;
    this.purchaseError = '';
    try {
      await this.store.buyCar(car.id);
      this.audio.sfx('buy');
      this.toast(`${car.name} added to your garage.`);
    } catch (err) {
      this.purchaseError = err instanceof ApiError ? err.message : 'Purchase failed.';
      this.audio.sfx('error');
    } finally {
      this.purchaseBusy = false;
    }
  }

  async buySelectedUpgrade(): Promise<void> {
    const def = UPGRADES[clamp(this.upgradeIndex, 0, UPGRADES.length - 1)];
    if (!def) return;
    this.purchaseBusy = true;
    this.purchaseError = '';
    try {
      const level = await this.store.buyUpgrade(this.store.save.selectedCar, def.id);
      this.audio.sfx('buy');
      this.toast(`${def.name} level ${level} installed.`);
    } catch (err) {
      this.purchaseError = err instanceof ApiError ? err.message : 'Purchase failed.';
      this.audio.sfx('error');
    } finally {
      this.purchaseBusy = false;
    }
  }

  // ------------------------------------------------------------ leaderboard
  async loadLeaderboard(trackId: string): Promise<void> {
    if (this.leaderboardTrack === trackId && !this.leaderboardLoading) return;
    this.leaderboardTrack = trackId;
    this.leaderboard = [];
    if (!this.store.signedIn) return;
    this.leaderboardLoading = true;
    try {
      this.leaderboard = await this.store.leaderboard(trackId);
    } catch {
      this.leaderboard = [];
    } finally {
      this.leaderboardLoading = false;
    }
  }

  // ------------------------------------------------------------ multiplayer
  /**
   * Lobby joins are driven by the server's answer, not by a fixed delay. The
   * old code fired a request and checked the result 500 ms later, so a slow
   * round trip (or a click before the socket had finished opening) silently
   * dropped the join and left the screen stuck on "joining…".
   */
  private beginLobbyIntent(kind: 'create' | 'join', action: () => void): void {
    this.lobbyIntent = kind;
    this.mpBusy = true;
    this.clearLobbyTimer();
    this.lobbyTimer = window.setTimeout(() => {
      this.lobbyIntent = null;
      this.mpBusy = false;
      this.lobbyTimer = null;
      this.toast('The race server did not answer. Try again.', true);
    }, 8000);
    action();
  }

  private settleLobbyIntent(): void {
    if (!this.lobbyIntent) return;
    this.lobbyIntent = null;
    this.mpBusy = false;
    this.clearLobbyTimer();
    if (this.net.lobby && (this.screen === 'multiplayer' || this.screen === 'lobby')) this.show('lobby');
  }

  private failLobbyIntent(message: string): void {
    if (!this.lobbyIntent) {
      this.toast(message, true);
      return;
    }
    this.lobbyIntent = null;
    this.mpBusy = false;
    this.clearLobbyTimer();
    this.toast(message, true);
  }

  private clearLobbyTimer(): void {
    if (this.lobbyTimer !== null) {
      window.clearTimeout(this.lobbyTimer);
      this.lobbyTimer = null;
    }
  }

  createLobby(): void {
    if (!this.net.connected) this.net.connect();
    this.beginLobbyIntent('create', () => {
      this.net.createLobby(this.lobbyDraft.name || 'Private room', {
        trackId: this.lobbyDraft.trackId,
        laps: this.lobbyDraft.laps,
        mode: 'online',
        aiCount: this.lobbyDraft.aiCount,
        aiDifficulty: this.lobbyDraft.difficulty,
        isPrivate: this.lobbyDraft.isPrivate,
        maxPlayers: this.lobbyDraft.maxPlayers,
      });
    });
  }

  joinLobby(code: string): void {
    if (!code || code.length < 3) {
      this.toast('Enter a lobby code.', true);
      return;
    }
    if (!this.net.connected) this.net.connect();
    const trimmed = code.trim().toUpperCase();
    this.beginLobbyIntent('join', () => this.net.joinLobby(trimmed));
  }

  leaveLobby(): void {
    this.lobbyIntent = null;
    this.mpBusy = false;
    this.clearLobbyTimer();
    this.net.leaveLobby();
    this.lobbyCodeEntry = '';
    this.show('multiplayer');
  }

  startOnlineRace(): void {
    const lobby = this.net.lobby;
    if (!lobby || !this.net.isHost()) return;
    this.net.startRace();
  }

  copyLobbyCode(): void {
    const code = this.net.lobby?.code ?? '';
    if (!code) return;
    void navigator.clipboard?.writeText(code).then(
      () => this.toast(`Lobby code ${code} copied.`),
      () => this.toast(`Lobby code: ${code}`),
    );
  }

  private onOnlineRaceStarted(): void {
    const lobby = this.net.lobby;
    if (!lobby) return;
    const carId = this.store.save.selectedCar;
    const car = getCar(carId);
    const session = new RaceSession(
      {
        mode: 'online',
        trackId: lobby.settings.trackId,
        laps: lobby.settings.laps,
        aiCount: lobby.settings.aiCount,
        aiDifficulty: lobby.settings.aiDifficulty,
        seed: 1,
        player: {
          name: this.store.user?.displayName ?? 'You',
          carId,
          color: this.store.save.paint[carId] ?? car.palettes[0]!,
          upgrades: this.store.save.upgrades[carId] ?? {},
        },
        displayName: this.store.user?.displayName ?? 'You',
        settings: this.store.settings,
        net: this.net,
        previousBestLap: this.store.save.bestLaps[lobby.settings.trackId] ?? 0,
      },
      this.audio,
    );
    for (const member of lobby.members) {
      const memberCar = CARS.find((c) => c.id === member.carId);
      session.registerRemoteMeta(member.id, member.name, member.color, memberCar?.sprite);
    }
    this.race?.dispose();
    this.race = session;
    this.raceHoldTimer = null;
    this.cameraMode = 0;
    this.paused = false;
    this.show('race');
    this.toast('Race starting!');
  }

  finishResults(): void {
    this.results = null;
    this.show('menu');
  }

  // ------------------------------------------------------------------ misc
  get trackCount(): number {
    return TRACKS.length;
  }

  get difficultyOrder(): AIDifficulty[] {
    return AI_ORDER;
  }

  /** Re-applies a locally cached save (used after an admin edits it). */
  reloadLocalSave(raw: unknown): void {
    this.store.save = migrateSave(raw);
    this.applySettings();
  }

  /** Enables or disables the on-screen touch controls. */
  setTouchControls(enabled: boolean): void {
    this.input.touch.enabled = enabled;
  }

  updateSettingsPatch(patch: Partial<GameSettings>): void {
    this.store.updateSettings(patch);
    this.applySettings();
  }

  /**
   * Called when the tab is shown or hidden.
   *
   * A backgrounded tab throttles `requestAnimationFrame` to roughly one frame
   * per second. Rather than let the race crawl along off-screen (or teleport
   * when it returns), a race in progress is paused — the same state Escape
   * produces, so the player sees exactly what they expect on the way back.
   */
  onVisibilityChange(hidden: boolean): void {
    this.audio.silence();
    if (!hidden) return;
    if (this.screen === 'race' && this.race && !this.paused) this.setPaused(true);
  }

  start(): void {
    this.input.dispose();
    this.text.dispose();
    this.audio.dispose();
    this.net.disconnect();
  }
}
